import { ComponentRef, inject, Injectable, TemplateRef, Type, ViewContainerRef } from '@angular/core';
import { ModalNamecardComponent } from '@base/components/modal-namecard/modal-namecard.component';
import { ModalProductInquiryViaExhibitorComponent } from '@base/components/modal-product-inquiry-via-exhibitor/modal-product-inquiry-via-exhibitor.component';
import { ModalAcceptRequestSuccessComponent } from '@base/components/private/modals/modal-accept-request-success/modal-accept-request-success.component';
import { ModalArchiveComponent } from '@base/components/private/modals/modal-archive/modal-archive.component';
import { ModalCancelMeetingComponent } from '@base/components/private/modals/modal-cancel-meeting/modal-cancel-meeting.component';
import { ModalDeclineRequestComponent } from '@base/components/private/modals/modal-decline-request/modal-decline-request.component';
import { ModalDownloadAppComponent } from '@base/components/private/modals/modal-download-app/modal-download-app.component';
import { ModalInformationComponent } from '@base/components/modal-information/modal-information.component';
import { ModalMapListComponent } from '@base/components/private/modals/modal-map-list/modal-map-list.component';
import { ModalMarkerListComponent } from '@base/components/private/modals/modal-marker-list/modal-marker-list.component';
import { ModalMeetFilterComponent } from '@base/components/private/modals/modal-meet-filter/modal-meet-filter.component';
import { ModalNewAuthComponent } from '@base/components/modal-new-auth/modal-new-auth.component';
import { ModalPeopleListComponent } from '@base/components/private/modals/modal-people-list/modal-people-list.component';
import { ModalPlannerGuidanceComponent } from '@base/components/private/modals/modal-planner-guidance/modal-planner-guidance.component';
import { ModalQrCodeComponent } from '@base/components/private/modals/modal-qr-code/modal-qr-code.component';
import { ModalMeetInterestComponent } from '@base/components/private/modals/modal-meet-interest/modal-meet-interest.component';
import { ModalRemoveAccountComponent } from '@base/components/private/modals/modal-remove-account/modal-remove-account.component';
import { ModalRemoveRequestMeetComponent } from '@base/components/private/modals/modal-remove-request-meet/modal-remove-request-meet.component';
import { BusinessMatchingComponent } from '@base/components/business-matching/business-matching.component';
import { ModalRequestMeetComponent } from '@base/components/modal-request-meet/modal-request-meet.component';
import { ModalRequestMeetSuccessComponent } from '@base/components/private/modals/modal-request-meet-success/modal-request-meet-success.component';
import { ModalSentFailedComponent } from '@base/components/private/modals/modal-sent-failed/modal-sent-failed.component';
import { ModalSentSuccessComponent } from '@base/components/private/modals/modal-sent-success/modal-sent-success.component';
import { ModalSourceRequestComponent } from '@base/components/private/modals/modal-source-request/modal-source-request.component';
import { ModalUnarchiveComponent } from '@base/components/private/modals/modal-unarchive/modal-unarchive.component';
import { ModalUndeclineRequestComponent } from '@base/components/private/modals/modal-undecline-request/modal-undecline-request.component';
import { ModalWarningForPreviewAccountComponent } from '@base/components/private/modals/modal-warning-for-preview-account/modal-warning-for-preview-account.component';
import { breakpoint, defaultModalConfig } from '@base/constants/common';
import { ModalConfig } from '@base/models/others/modal-config';
import { ModalProps } from '@base/models/others/modal.interface';
import { ChatComponent } from '@navbar/chat/chat.component';
import { UserComponent } from '@navbar/user/user.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { merge, take } from 'rxjs';
import { ExhibitorDetailComponent } from '@pages/meet/exhibitor-list/exhibitor-detail/exhibitor-detail.component';
import { PeopleDetailComponent } from '@pages/meet/people-list/people-detail/people-detail.component';
import { ProductDetailComponent } from '@pages/meet/product-list/product-detail/product-detail.component';
import { ModalRateMeetingComponent } from '@base/components/private/modals/modal-rate-meeting/modal-rate-meeting.component';
import { ModalAgendaFilterComponent } from '@base/components/modal-agenda-filter/modal-agenda-filter.component';
import { SpeakerDetailComponent } from '@pages/agenda/speaker-list/speaker-detail/speaker-detail.component';
import { ModalEditCompanyComponent } from '@base/components/modal-edit-company/modal-edit-company.component';
import { ModalEditCompanyImageComponent } from '@base/components/modal-edit-company-image/modal-edit-company-image.component';
import { ModalUploadCompanyBrochureComponent } from '@base/components/modal-upload-company-brochure/modal-upload-company-brochure.component';
import { ModalEditProfileComponent } from '@base/components/modal-edit-profile/modal-edit-profile.component';
import { ModalInfoMeetingNewTabComponent } from '@base/components/private/modals/modal-info-meeting-new-tab/modal-info-meeting-new-tab.component';
import { ModalAgendaInterestComponent } from '@base/components/modal-agenda-interest/modal-agenda-interest.component';
import { ModalMediaPipComponent } from '@base/components/modal-media-pip/modal-media-pip.component';
import { ModalEditProductComponent } from '@base/components/modal-edit-product/modal-edit-product.component';
import { AgendaDetailComponent } from '@pages/agenda/agenda-list/agenda-detail/agenda-detail.component';
import { GlobalStateService } from './global-state.service';
import { ModalOrganizerComponent } from '@base/components/private/modals/modal-organizer/modal-organizer.component';
import { ModalConfirmationAiComponent } from '@base/components/modal-confirmation-ai/modal-confirmation-ai.component';
import { ModalExternalFloorplanComponent } from '@base/components/modal-external-floorplan/modal-external-floorplan.component';
import { ModalExternalChatComponent } from '@base/components/modal-external-chat/modal-external-chat.component';
import { ModalServicePort } from './modal-service.token';

@Injectable({
  providedIn: 'root',
})
export class ModalService implements ModalServicePort {
  private readonly modalSrv = inject(NgbModal);
  private refs: { modalId?: string; ref: NgbModalRef }[] = [];
  private isDocViewerOpening = false;
  private readonly globalStateSrv = inject(GlobalStateService);
  // start of custom cosmoprof-asia-2026: https://jublia.atlassian.net/browse/JO-XXXX
  private keepNavbarForMfe = false;

  setKeepNavbarForMfe(enabled: boolean): void {
    this.keepNavbarForMfe = enabled;
  }

  isKeepNavbarForMfe(): boolean {
    return this.keepNavbarForMfe;
  }

  private get mfeNavbarConfig(): Partial<ModalConfig> {
    if (!this.keepNavbarForMfe) return {};
    return { windowClass: 'modal-keep-navbar', backdropClass: 'modal-keep-navbar-backdrop' };
  }

  showModalExternalChat({ addsData }: Partial<ModalProps> = {}): NgbModalRef | undefined {
    return this.showStandardModal({
      component: ModalExternalChatComponent,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-external-chat', fullscreen: true, ...this.mfeNavbarConfig },
    });
  }
  // end of custom cosmoprof-asia-2026: https://jublia.atlassian.net/browse/JO-XXXX

  private readonly loadDocViewer = async () => {
    const { DocViewerComponent } = await import('@base/components/private/modals/doc-viewer/doc-viewer.component');
    return DocViewerComponent;
  };

  /**
   * Opens a modal dialog with the specified component as its content.
   *
   * @typeParam T - The type of the component to render inside the modal.
   * @param component - The component type to instantiate and display in the modal.
   * @param inputs - Optional. An object containing input properties to assign to the component instance.
   * @param config - Optional. Additional configuration options for the modal.
   * @returns A reference to the newly opened modal.
   */
  open<T>(
    component: Type<T> | ViewContainerRef | ComponentRef<T> | TemplateRef<T>,
    inputs?: Partial<T> | null | undefined,
    config?: ModalConfig
  ): NgbModalRef {
    // override default config if on mobile screen then make it fullscreen
    const isFullScreen = this.globalStateSrv.isFullScreen();
    if (window.innerWidth <= breakpoint.MD || isFullScreen) {
      const isModalAuth = config?.modalId === 'jublia-auth';
      config = {
        ...config,
        fullscreen: isModalAuth ? false : true,
        keyboard: false,
        backdrop: 'static',
      };
    }

    const modalRef = this.modalSrv.open(component, config);
    this.refs.push({
      modalId: config?.modalId ?? '',
      ref: modalRef,
    });
    merge(modalRef.closed, modalRef.dismissed)
      .pipe(take(1))
      .subscribe(() => {
        this.refs = this.refs.filter((item) => item.ref !== modalRef);
      });
    if (inputs) {
      // NOTE: `_contentRef.componentRef` is a private ng-bootstrap field (no public API exists).
      // If ng-bootstrap restructures this in a future major version, `componentRef` will be
      // undefined and the setInput path silently falls back to direct property assignment.
      // Signal inputs (readonly getters) are NOT settable via direct assignment, so those inputs
      // would receive their default values. Re-validate this path after any ng-bootstrap upgrade.
      const componentRef = (modalRef as unknown as { _contentRef?: { componentRef?: ComponentRef<T> } })._contentRef
        ?.componentRef;
      if (!componentRef) {
        console.warn(
          'modal.service: ng-bootstrap _contentRef.componentRef unavailable — signal inputs will not be set'
        );
      }
      Object.entries(inputs).forEach(([key, value]) => {
        if (componentRef) {
          try {
            // Works for both @Input() decorator and signal input()
            componentRef.setInput(key, value);
          } catch {
            // Key is not a declared @Input — fall back to direct assignment for public properties
            if (key in (modalRef?.componentInstance ?? {})) {
              (modalRef.componentInstance as T)[key as keyof T] = value as T[keyof T];
            }
          }
        } else if (modalRef?.componentInstance !== undefined && key in modalRef.componentInstance) {
          (modalRef.componentInstance as T)[key as keyof T] = value as T[keyof T];
        }
      });
    }

    return modalRef;
  }

  /**
   * Closes a modal by ID or the most recently opened modal.
   *
   * @param modalId - Optional modal identifier to close. If omitted, closes the latest modal.
   * @param data - Optional data to pass to the modal when closing.
   * @returns void
   */
  close(modalId?: string, data?: unknown): void {
    // Select the requested active modal, or the latest active modal when no ID is supplied.
    const modalRef = modalId ? this.refs.find((item) => item.modalId === modalId)?.ref : this.refs.at(-1)?.ref;
    modalRef?.close(data);
  }

  /**
   * Close all modal refs except those whose `modalId` is listed in `unless`.
   * @param excludeIds - Modal IDs to keep open
   */
  closeAll(excludeIds: string[] = ['jublia-chat']): void {
    const skip = new Set(excludeIds); // fast lookup, handles empty list too

    // Close a snapshot so lifecycle callbacks can update this.refs during iteration.
    [...this.refs].forEach((item) => {
      if (!skip.has(item.modalId ?? '')) {
        item.ref?.close?.();
      }
    });
  }

  /**
   * Gets the list of currently opened modal references.
   * @returns the list of modal references
   */
  getModalRefs() {
    return this.refs;
  }

  /**
   * Opens a modal with default configuration merged with overrides.
   *
   * @param component - The component to render inside the modal.
   * @param addsConfig - Optional modal configuration overrides.
   * @param addsData - Optional inputs passed to the component instance.
   * @returns The modal reference, or `undefined` if no component is provided.
   */
  private showStandardModal({ component, addsConfig, addsData }: Partial<ModalProps> = {}): NgbModalRef | undefined {
    if (!component) return;

    const config: ModalConfig = {
      ...defaultModalConfig,
      ...(addsConfig ?? {}),
    };

    const data = {
      modalId: addsConfig?.modalId,
      ...(addsData ?? {}),
    };

    return this.open(component, data, config);
  }

  /**
   * Opens the "Sent Failed" modal.
   */
  showModalFailedRequest({ component, addsData, addsConfig }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalSentFailedComponent;
    }
    return this.showStandardModal({
      component,
      addsData,
      addsConfig: { modalId: 'sent-failed', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the "Success Request" modal.
   */
  showModalSuccessRequest({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalSentSuccessComponent;
    }
    return this.showStandardModal({
      component,
      addsData,
      addsConfig: { modalId: 'success-request', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the "Request Meet" modal.
   * When `matchmaking_improvements` is enabled in the loader, always opens the business
   * matching MFE wrapper — overriding any caller-supplied component override.
   *
   * NOTE: When BM flag is active, any `component` argument passed by the caller (including
   * custom-event `modalRequestMeet` overrides set via `CardPeopleAbstract.modalRequestMeet`)
   * is silently replaced with `BusinessMatchingComponent`. Custom events that enable this
   * flag must not rely on passing their own component here.
   */
  showModalRequestMeet({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (this.globalStateSrv.isMatchmakingImprovements()) {
      component = BusinessMatchingComponent;
    } else if (!component) {
      component = ModalRequestMeetComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { isCloseAll: true, ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-meet', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the "Remove Request Meet" modal.
   */
  showModalRemoveRequestMeet({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalRemoveRequestMeetComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-removed-request', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the "Request Meet Success" modal.
   */
  showModalRequestMeetSuccess({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalRequestMeetSuccessComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-sent-request-success', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the "People List" modal.
   */
  showModalPeopleList({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalPeopleListComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-people-list', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the one-time Planner guidance modal.
   */
  showModalPlannerGuidance({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalPlannerGuidanceComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-planner-guidance', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the "Product Inquiry via Exhibitor" modal.
   */
  showModalProductInquiry({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalProductInquiryViaExhibitorComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { isCloseAll: true, ...(addsData ?? {}) },
      addsConfig: { modalId: 'product-inquiry-via-exhibitor', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the "Exhibitor Namecard" modal.
   */
  showModalNameCard({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalNamecardComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'exhibitor-namecard', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the chat modal.
   */
  showModalChat({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ChatComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'jublia-chat', fullscreen: true, scrollable: false, ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the modal organizer.
   */
  showModalOrganizer({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalOrganizerComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-organizer', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the product detail modal.
   */
  showModalProductDetail({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ProductDetailComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'product-detail', size: 'xl', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the people detail modal.
   */
  showModalPeopleDetail({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = PeopleDetailComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'people-detail', size: 'xl', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the agenda detail modal.
   */
  showModalAgendaDetail({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = AgendaDetailComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { isCloseAll: true, ...(addsData ?? {}) },
      addsConfig: { modalId: 'agenda-detail', size: 'xl', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the QR code modal.
   */
  showModalQrCode({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalQrCodeComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'qr-code', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the user edit modal.
   */
  showModalEdit({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = UserComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'user-edit', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the new authentication modal.
   */
  showModalNewAuth({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalNewAuthComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { backdropClass: 'bg-new-auth-overlay', modalId: 'jublia-auth', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the cancel meeting modal.
   * When `matchmaking_improvements` is enabled in the loader, opens the business
   * matching MFE wrapper instead of the legacy cancel modal.
   */
  showModalCancelMeeting({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (this.globalStateSrv.isMatchmakingImprovements()) {
      component = BusinessMatchingComponent;
    } else if (!component) {
      component = ModalCancelMeetingComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-cancel-meeting', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the archive modal.
   */
  showModalArchive({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalArchiveComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-archive', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the unarchive modal.
   */
  showModalUnarchive({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalUnarchiveComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-unarchive', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the decline request modal.
   * When `matchmaking_improvements` is enabled in the loader, opens the business
   * matching MFE wrapper instead of the legacy decline modal.
   */
  showModalDecline({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (this.globalStateSrv.isMatchmakingImprovements()) {
      component = BusinessMatchingComponent;
    } else if (!component) {
      component = ModalDeclineRequestComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-decline-request', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the undecline request modal.
   * When `matchmaking_improvements` is enabled in the loader, opens the business
   * matching MFE wrapper instead of the legacy undecline modal.
   */
  showModalUndecline({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (this.globalStateSrv.isMatchmakingImprovements()) {
      component = BusinessMatchingComponent;
    } else if (!component) {
      component = ModalUndeclineRequestComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-undecline-request', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the accept request success modal.
   */
  showModalAcceptRequestSuccess({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalAcceptRequestSuccessComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-accept-request-success', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the marker list modal.
   */
  showModalMarkerList({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalMarkerListComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'marker-list', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the map list modal.
   */
  showModalMapList({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalMapListComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'map-list', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the exhibitor detail modal.
   */
  showModalExhibitorDetail({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ExhibitorDetailComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'exhibitor-detail', size: 'xl', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the document viewer modal.
   */
  async showModalDocViewer({ component, addsConfig, addsData }: Partial<ModalProps> = {}): Promise<
    NgbModalRef | undefined
  > {
    if (this.isDocViewerOpening) return undefined;

    this.isDocViewerOpening = true;
    if (!component) {
      try {
        component = await this.loadDocViewer();
      } catch (error: unknown) {
        this.isDocViewerOpening = false;
        console.error('modal.service: failed to load document viewer', error);
        this.showModalInformation({ addsData: { message: 'SOMETHING_WENT_WRONG' } });
        return undefined;
      }
    }

    try {
      const modalRef = this.showStandardModal({
        component,
        addsData: { ...(addsData ?? {}) },
        addsConfig: { modalId: 'doc-viewer', fullscreen: true, ...(addsConfig ?? {}) },
      });

      if (!modalRef) {
        this.isDocViewerOpening = false;
        return undefined;
      }

      modalRef.hidden.subscribe(() => {
        this.isDocViewerOpening = false;
      });
      return modalRef;
    } catch (error: unknown) {
      this.isDocViewerOpening = false;
      throw error;
    }
  }

  /**
   * Opens the sourcing request modal.
   */
  showModalSourcingRequest({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalSourceRequestComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'sourcing-request', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the recommendation interests modal.
   */
  showModalRecommendationInterests({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalMeetInterestComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'meet-recommendation-interests', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the meet filter modal.
   */
  showModalMeetFilter({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalMeetFilterComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'meet-filter', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the agenda filter modal.
   */
  showModalAgendaFilter({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalAgendaFilterComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'agenda-filter', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the download app modal.
   */
  showModalDownloadApp({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalDownloadAppComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'download-app', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the remove account modal.
   */
  showModalRemoveAccount({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalRemoveAccountComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'remove-account', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the warning for preview account modal.
   */
  showModalWarningForPreviewAccount({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalWarningForPreviewAccountComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'warning-for-preview', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the modal for any information or warning
   */
  showModalInformation({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalInformationComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-information', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the edit company modal.
   */
  showModalEditCompany({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalEditCompanyComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'edit-company', size: 'xl', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the edit company modal.
   */
  showModalEditCompanyImage({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalEditCompanyImageComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'edit-company-image', size: 'xl', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the company brochure upload modal.
   */
  showModalUploadBrochure({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalUploadCompanyBrochureComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'upload-brochure', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the modal for rate meeting
   */
  showModalRateMeeting({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalRateMeetingComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-rate-meeting', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the pop-up meeting modal in a new tab.
   */
  showModalPopUpMeetingNewTab({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalInfoMeetingNewTabComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-info-meeting-new-tab', size: 'md', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the modal for rate meeting
   */
  showModalEditProfile({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalEditProfileComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-edit-profile', size: 'xl', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the modal for edit product
   */
  showModalEditProduct({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalEditProductComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-edit-product', size: 'xl', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the speaker detail modal.
   */
  showModalSpeakerDetail({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = SpeakerDetailComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}), isCloseAll: true },
      addsConfig: { modalId: 'modal-speaker', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens agenda interest for you modal.
   */
  showModalAgendaInterestForYou({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalAgendaInterestComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-agenda-interest', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens agenda interest for you modal.
   */
  showModalPIP({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalMediaPipComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-media-pip', size: 'lg', ...(addsConfig ?? {}) },
    });
  }

  /**
   * Opens the Informa Floorplan MFE modal fullscreen.
   * Pass `floorplanId` and/or `markerId` in `addsData` to deep-link to a specific floor/marker.
   */
  showModalExternalFloorplan({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalExternalFloorplanComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      // start of custom cosmoprof-asia-2026: https://jublia.atlassian.net/browse/JO-XXXX
      // mfeNavbarConfig spreads windowClass/backdropClass only when Cosmoprof group is confirmed.
      addsConfig: {
        modalId: 'modal-external-floorplan',
        fullscreen: true,
        ...this.mfeNavbarConfig,
        ...(addsConfig ?? {}),
      },
      // end of custom cosmoprof-asia-2026: https://jublia.atlassian.net/browse/JO-XXXX
    });
  }

  /**
   * Opens the chat modal.
   */
  showModalConfirmationAI({ component, addsConfig, addsData }: Partial<ModalProps> = {}) {
    if (!component) {
      component = ModalConfirmationAiComponent;
    }
    return this.showStandardModal({
      component,
      addsData: { ...(addsData ?? {}) },
      addsConfig: { modalId: 'modal-confirmation-ai', size: 'md', ...(addsConfig ?? {}) },
    });
  }
}
